import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../../../theme';
import Text from '../../../../theme/text/Text';
import Shbutton from '../../../../theme/buttons/Shbutton';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { useTestShipperConnectionMutation } from '../../../../../newapi/shipper/shipperSlice';
import { validateShipperFormData, getShipperDisplayName } from '../../../../../utils/shipperValidation';

const UniversalConnectionTester = ({ 
    shipperType, 
    formData, 
    onTestResult, 
    disabled = false, 
    className = '' 
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const colors = tokens(theme.palette.mode);
    const [testShipperConnection, { isLoading }] = useTestShipperConnectionMutation();
    const [testResult, setTestResult] = useState(null);

    const handleTestConnection = async () => {
        if(isLoading) return;
        try {
            // Validate form data before testing
            const validation = validateShipperFormData(shipperType, formData);
            if (!validation.isValid) {
                const errorResult = {
                    success: false,
                    message: t('invalid_credentials'),
                    errors: validation.errors
                };
                setTestResult(errorResult);
                if (onTestResult) {
                    onTestResult(errorResult);
                }
                return;
            }

            const testData = {
                shipperType,
                credentials: formData,
                timeout: 30000
            };

            const result = await testShipperConnection(testData).unwrap();
            
            const testResultData = {
                success: result.success,
                message: result.message,
                details: result.data || {}
            };
            
            setTestResult(testResultData);
            
            if (onTestResult) {
                onTestResult(testResultData);
            }
        } catch (error) {
            const errorResult = {
                success: false,
                message: error.data?.message || t('shipper_connection_test_failed'),
                details: error.data?.details || {}
            };
            
            setTestResult(errorResult);
            
            if (onTestResult) {
                onTestResult(errorResult);
            }
        }
    };

    const isFormValid = () => {
        const validation = validateShipperFormData(shipperType, formData);
        return validation.isValid;
    };

    const getStatusIcon = () => {
        if (!testResult) return null;
        
        if (testResult.success) {
            return <CheckCircle sx={{ color: colors.green[400], fontSize: 20 }} />;
        } else {
            return <Error sx={{ color: colors.red[400], fontSize: 20 }} />;
        }
    };

    const getStatusColor = () => {
        if (!testResult) return colors.grey[400];
        return testResult.success ? colors.green[400] : colors.red[400];
    };

    const shipperDisplayName = getShipperDisplayName(shipperType);

    return (
        <Box 
            className={className}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                backgroundColor: colors.grey[50],
                borderRadius: 2,
                border: `1px solid ${colors.grey[200]}`,
                mt: 2
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Text weight="bold">
                    {t('shipper_connection_test', { shipper: shipperDisplayName })}
                </Text>
                {getStatusIcon()}
            </Box>
            
            <Text size="small" color={colors.grey[600]}>
                {t('shipper_connection_test_description')}
            </Text>

            {testResult && (
                <Box sx={{
                    p: 1.5,
                    backgroundColor: testResult.success ? colors.green[50] : colors.red[50],
                    borderRadius: 1,
                    border: `1px solid ${getStatusColor()}`
                }}>
                    <Text 
                        size="small" 
                        color={getStatusColor()}
                        weight="medium"
                    >
                        {testResult.message}
                    </Text>
                    
                    {testResult.details && Object.keys(testResult.details).length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                            {/* Basic Connection Info */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                                {/* {testResult.details.responseTime && (
                                    <Text size="xs" color={colors.grey[600]}>
                                        {t('response_time')}: {testResult.details.responseTime}ms
                                    </Text>
                                )}*/}
                                {testResult.details.apiVersion && (
                                    <Text size="xs" color={colors.grey[600]}>
                                        {t('api_version')}: {testResult.details.apiVersion}
                                    </Text>
                                )}
                                {testResult.details.environment && (
                                    <Text size="xs" color={colors.grey[600]}>
                                        {t('environment')}: {testResult.details.environment}
                                    </Text>
                                )}
                                {testResult.details.tokenExpiry && (
                                    <Text size="xs" color={colors.grey[600]}>
                                        {t('token_expires')}: {testResult.details.tokenExpiry}
                                    </Text>
                                )}
                            </Box>

                            {/* Account Status */}
                            {testResult.details.accountStatus && (
                                <Box sx={{ mb: 1 }}>
                                    <Text size="xs" color={colors.grey[600]}>
                                        {t('account_status')}: 
                                        <Text 
                                            component="span" 
                                            size="xs" 
                                            color={testResult.details.accountStatus === 'active' ? colors.green[500] : colors.orange[500]}
                                            weight="medium"
                                            sx={{ ml: 0.5 }}
                                        >
                                            {t(`account_status_${testResult.details.accountStatus}`)}
                                        </Text>
                                    </Text>
                                </Box>
                            )}

                            {/* API Connectivity Status */}
                            {typeof testResult.details.apiConnectivity === 'boolean' && (
                                <Box sx={{ mb: 1 }}>
                                    <Text size="xs" color={colors.grey[600]}>
                                        {t('api_connectivity')}: 
                                        <Text 
                                            component="span" 
                                            size="xs" 
                                            color={testResult.details.apiConnectivity ? colors.green[500] : colors.red[500]}
                                            weight="medium"
                                            sx={{ ml: 0.5 }}
                                        >
                                            {testResult.details.apiConnectivity ? t('connected') : t('disconnected')}
                                        </Text>
                                    </Text>
                                </Box>
                            )}

                            {/* Customer Information */}
                            {testResult.details.customerInfo && (
                                <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: colors.blue[25], borderRadius: 1, border: `1px solid ${colors.blue[100]}` }}>
                                    <Text size="xs" weight="medium" color={colors.blue[600]} sx={{ mb: 1, display: 'block' }}>
                                        {t('customer_information')}:
                                    </Text>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {testResult.details.customerInfo.customerNumber && (
                                            <Text size="xs" color={colors.grey[600]} sx={{ display: 'block' }}>
                                                {t('customer_number')}: {testResult.details.customerInfo.customerNumber}
                                            </Text>
                                        )}
                                        {testResult.details.customerInfo.customerCode && (
                                            <Text size="xs" color={colors.grey[600]} sx={{ display: 'block' }}>
                                                {t('customer_code')}: {testResult.details.customerInfo.customerCode}
                                            </Text>
                                        )}
                                        {typeof testResult.details.customerInfo.validated === 'boolean' && (
                                            <Text size="xs" color={colors.grey[600]} sx={{ display: 'block' }}>
                                                {t('validated')}: 
                                                <Text 
                                                    component="span" 
                                                    size="xs" 
                                                    color={testResult.details.customerInfo.validated ? colors.green[500] : colors.red[500]}
                                                    weight="medium"
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    {testResult.details.customerInfo.validated ? t('yes') : t('no')}
                                                </Text>
                                            </Text>
                                        )}
                                        {testResult.details.customerInfo.contractTypes && testResult.details.customerInfo.contractTypes.length > 0 && (
                                            <Text size="xs" color={colors.grey[600]} sx={{ display: 'block' }}>
                                                {t('contract_types')}: {testResult.details.customerInfo.contractTypes.join(', ')}
                                            </Text>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Available Services */}
                            {testResult.details.availableServices && testResult.details.availableServices.length > 0 && (
                                <Box sx={{ p: 1, backgroundColor: colors.green[25], borderRadius: 1, border: `1px solid ${colors.green[100]}` }}>
                                    <Text size="xs" weight="medium" color={colors.green[600]} sx={{ mb: 0.5 }}>
                                        {t('available_services')} ({testResult.details.availableServices.length}):
                                    </Text>
                                    <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                                        {testResult.details.availableServices.map((service, index) => (
                                            <Text key={index} size="xs" color={colors.grey[600]} sx={{ display: 'block' }}>
                                                • {service.code}: {service.name}
                                            </Text>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {shipperType === 'sendcloud' && typeof testResult.details.user_id === 'number' && (
                                <Box sx={{ mb: 1 }}>
                                    <Text size="xs" color={colors.grey[600]}>
                                        {t('user_id')}:
                                        <Text
                                            component="span"
                                            size="xs"
                                            color={testResult.user_id > 0 ? colors.green[500] : colors.orange[500]}
                                            weight="medium"
                                            sx={{ ml: 0.5 }}
                                        >
                                            {testResult.details.user_id}
                                        </Text>
                                    </Text>
                                </Box>
                            )}

                            {/* SendCloud-specific Shipping Methods Count */}
                            {shipperType === 'sendcloud' && typeof testResult.details.shippingMethods === 'number' && (
                                <Box sx={{ mb: 1 }}>
                                    <Text size="xs" color={colors.grey[600]}>
                                        {t('shipping_methods_available')}:
                                        <Text
                                            component="span"
                                            size="xs"
                                            color={testResult.shippingMethods > 0 ? colors.green[500] : colors.orange[500]}
                                            weight="medium"
                                            sx={{ ml: 0.5 }}
                                        >
                                            {testResult.details.shippingMethods}
                                        </Text>
                                    </Text>
                                </Box>
                            )}

                            {/* SendCloud-specific Integrations */}
                            {shipperType === 'sendcloud' && testResult.details.integrations && (
                                <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: colors.orange[25], borderRadius: 1, border: `1px solid ${colors.orange[100]}` }}>
                                    <Text size="xs" weight="medium" color={colors.orange[600]} sx={{ mb: 1, display: 'block' }}>
                                        {t('integrations')}: {testResult.details.integrations}
                                    </Text>
                                </Box>
                            )}
                        </Box>
                    )}
                    
                    {testResult.errors && testResult.errors.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {testResult.errors.map((error, index) => (
                                <Text key={index} size="xs" color={colors.red[400]}>
                                    • {t(error)}
                                </Text>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            <Shbutton
                isLoading={isLoading}
                variant="outlined"
                onClick={handleTestConnection}
                disabled={!isFormValid() || isLoading || disabled}
                startIcon={isLoading ? <CircularProgress size={16} /> : null}
                sx={{
                    alignSelf: 'flex-start',
                    borderColor: colors.blue[400],
                    color: colors.blue[400],
                    '&:hover': {
                        backgroundColor: colors.blue[50],
                        borderColor: colors.blue[500]
                    },
                    '&:disabled': {
                        borderColor: colors.grey[300],
                        color: colors.grey[400]
                    }
                }}
            >
                {isLoading ? t('testing_connection') : t('test_connection')}
            </Shbutton>
        </Box>
    );
};

export default UniversalConnectionTester;